import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { ArrowUpRight, Check, FileText, Loader2, RefreshCw, Sparkles, User, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { werseeAi, type StudioJob } from '../../ai/client';
import type { StudioMessage, StudioProduct, StudioReply } from '../../../supabase/functions/wersee-ai/studioContract';
import { referenceImagesData, saveStudioProduct, type StudioAssetProgress } from '../../services/productStudio';
import { StudioComposer } from './StudioComposer';
import { useCreationSurface } from './useCreationSurface';
import { writeStudioCollection, type StudioCollection } from '../../lib/productStudioCollection';

export function ProductStudio({ initialPrompt, initialFiles = [], initialCollection, initialProjectId, onClose }: { initialPrompt?: string; initialFiles?: File[]; initialCollection?: StudioCollection; initialProjectId?: string; onClose: () => void }) {
  const { user } = useAuth();
  const surface = useCreationSurface();
  const [messages, setMessages] = useState<StudioMessage[]>(initialCollection ? [{ role: 'user', content: initialCollection.prompt }, { role: 'assistant', content: initialCollection.message }] : []);
  const [prompt, setPrompt] = useState(''); const [files, setFiles] = useState<File[]>(initialFiles);
  const [busy, setBusy] = useState(false); const [error, setError] = useState(''); const [status, setStatus] = useState('');
  const [reply, setReply] = useState<StudioReply | null>(null); const [products, setProducts] = useState<StudioProduct[]>(initialCollection?.products || []);
  const [progress, setProgress] = useState<StudioAssetProgress[]>(initialCollection?.progress || []);
  const [activeThumb, setActiveThumb] = useState<Record<number, number>>({});
  const [job, setJob] = useState<StudioJob | null>(null);
  const projectRef = useRef(initialProjectId || initialCollection?.projectId);
  const revisionRef = useRef<string | undefined>(undefined);
  const checkpointRef = useRef<Promise<unknown>>(Promise.resolve());
  const progressRef = useRef<StudioAssetProgress[]>(initialCollection?.progress || []); const collectionRef = useRef<StudioCollection | null>(initialCollection || null); const imageRef = useRef<string[]>([]); const lastRequest = useRef<{ messages: StudioMessage[]; forceGenerate?: boolean; files: File[] } | null>(null);
  const started = useRef(false); const mounted = useRef(true); const controller = useRef<AbortController | null>(null); const lock = useRef(false);
  const end = useRef<HTMLDivElement>(null); const reduced = useReducedMotion();

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; controller.current?.abort(); }; }, []);
  useEffect(() => { end.current?.scrollIntoView({ block: 'end', behavior: reduced ? 'instant' : 'smooth' }); }, [messages, reply, status, reduced]);

  const updateProgress = (index: number, value: StudioAssetProgress) => {
    progressRef.current[index] = value;
    if (user && collectionRef.current) {
      collectionRef.current = { ...collectionRef.current, progress: [...progressRef.current] };
      writeStudioCollection(user.id, collectionRef.current);
    }
    if (mounted.current) setProgress([...progressRef.current]);
    if (projectRef.current) {
      const id = projectRef.current; const progress = [...progressRef.current];
      checkpointRef.current = checkpointRef.current.catch(() => undefined).then(() => werseeAi.updateStudioProject(id, 'checkpoint', { progress })).catch(() => {
        if (mounted.current) setError('Bestanden zijn opgeslagen, maar de voortgang kon niet synchroniseren. Probeer opnieuw.');
      });
    }
  };

  const saveProducts = async (items: StudioProduct[]) => {
    if (!user) throw new Error('Sign in to save your products.');
    for (let i = 0; i < items.length; i++) {
      if (progressRef.current[i]?.complete) continue;
      if (mounted.current) setStatus(`Genereren van thumbnails en PDF · ${i + 1} van ${items.length}`);
      await saveStudioProduct(items[i], user.id, progressRef.current[i], value => updateProgress(i, value), undefined, step => {
        if (mounted.current) setStatus(`${step} · ${i + 1} van ${items.length}`);
      });
    }
    await checkpointRef.current;
    if (mounted.current) setStatus('Je bestanden en Marketplace-concept staan klaar voor controle.');
  };

  const followProject = async (id: string): Promise<StudioJob | null> => {
    while (mounted.current) {
      const { project } = await werseeAi.getStudioProject(id);
      if (!mounted.current) return null;
      setJob(project); setStatus(project.stage);
      if (project.state === 'failed' && project.attempts < 3 && project.retryAt) {
        setStatus('Tijdelijk onderbroken · Buddy hervat vanaf het laatste checkpoint…');
      } else if (!['queued', 'running'].includes(project.state)) return project;
      await new Promise<void>(resolve => window.setTimeout(resolve, 2000));
    }
    return null;
  };

  const applyProject = async (project: StudioJob, next: StudioMessage[]) => {
    if (project.state === 'failed') throw new Error(project.error || 'Generatie is onderbroken. Probeer opnieuw.');
    if (project.state === 'cancelled') { setStatus('Creatie geannuleerd.'); return; }
    if (!project.result) throw new Error('Het project bevat nog geen resultaat.');
    const result = project.result;
    setReply(result); setMessages([...next, { role: 'assistant', content: result.phase === 'question' ? `${result.message}\n\n${result.question}` : result.message }]);
    if (result.phase === 'ready') {
      setProducts(result.products);
      progressRef.current = project.progress?.length === result.products.length ? project.progress : result.products.map(() => ({ id: crypto.randomUUID() }));
      setProgress([...progressRef.current]);
      collectionRef.current = { projectId: project.id, createdAt: Date.now(), prompt: initialPrompt || next.find(message => message.role === 'user')?.content || project.title, message: result.message, products: result.products, progress: [...progressRef.current] };
      if (user) writeStudioCollection(user.id, collectionRef.current);
      // Persist stable artifact/draft IDs before uploading so reloads reuse them.
      await werseeAi.updateStudioProject(project.id, 'checkpoint', { progress: [...progressRef.current] });
      await saveProducts(result.products);
    } else setStatus('Buddy heeft één antwoord nodig om verder te gaan.');
  };

  const request = async (next: StudioMessage[], attachments: File[], forceGenerate = false) => {
    if (lock.current) return; lock.current = true; setBusy(true); setError(''); setStatus('Wersee AI werkt je concept uit…');
    lastRequest.current = { messages: next, forceGenerate, files: attachments };
    controller.current = new AbortController();
    try {
      if (attachments.length) imageRef.current = [...imageRef.current, ...await referenceImagesData(attachments)].slice(-4);
      lastRequest.current.files = [];
      const id = projectRef.current || crypto.randomUUID();
      projectRef.current = id;
      if (job?.state === 'awaiting_input') {
        await werseeAi.updateStudioProject(id, 'answer', { answer: forceGenerate ? 'Maak het product met je aanbevolen keuzes.' : next.at(-1)?.content || 'Maak het product', forceGenerate });
      } else {
        await werseeAi.startStudioProject(id, { messages: next.slice(-9), images: imageRef.current, forceGenerate }, revisionRef.current);
      }
      const project = await followProject(id);
      if (project && mounted.current) await applyProject(project, next);
    } catch (reason) { if (mounted.current && !(reason instanceof DOMException && reason.name === 'AbortError')) { setError(reason instanceof Error ? reason.message : 'Creatie kon niet voltooien. Probeer het opnieuw.'); setStatus('Je concept is bewaard.'); } }
    finally { lock.current = false; if (mounted.current) setBusy(false); }
  };

  const send = (text = prompt) => {
    if (!text.trim() || busy) return;
    if (products.length && progressRef.current.every(item => item.complete) && projectRef.current) {
      revisionRef.current = projectRef.current; projectRef.current = crypto.randomUUID(); setJob(null);
    }
    const next: StudioMessage[] = [...messages, { role: 'user', content: text.trim() }];
    setMessages(next); setPrompt(''); const attachments = files; setFiles([]); void request(next, attachments, products.length > 0);
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (started.current) return;
      if (initialCollection) {
        started.current = true; lock.current = true; setBusy(true);
        void saveProducts(initialCollection.products).catch(reason => {
          if (mounted.current) setError(reason instanceof Error ? reason.message : 'Kon collectie niet opslaan.');
        }).finally(() => { lock.current = false; if (mounted.current) setBusy(false); });
      } else if (initialProjectId) {
        started.current = true; lock.current = true; setBusy(true);
        void followProject(initialProjectId).then(project => project && mounted.current ? applyProject(project, [{ role: 'user', content: project.title }]) : undefined)
          .catch(reason => { if (mounted.current) setError(reason instanceof Error ? reason.message : 'Kon project niet laden.'); })
          .finally(() => { lock.current = false; if (mounted.current) setBusy(false); });
      } else if (initialPrompt) { started.current = true; send(initialPrompt); }
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);

  const retry = async () => {
    if (projectRef.current && job?.state === 'failed') {
      if (lock.current) return; lock.current = true; setBusy(true); setError('');
      try {
        await werseeAi.updateStudioProject(projectRef.current, 'retry');
        const project = await followProject(projectRef.current);
        if (project && mounted.current) await applyProject(project, messages.filter(message => message.role === 'user'));
      } catch (reason) { setError(reason instanceof Error ? reason.message : 'Opnieuw proberen is nog niet beschikbaar.'); }
      finally { lock.current = false; if (mounted.current) setBusy(false); }
      return;
    }
    if (products.length) { if (lock.current) return; lock.current = true; setBusy(true); setError(''); try { await saveProducts(products); } catch (reason) { setError(reason instanceof Error ? reason.message : 'Kon producten niet opslaan.'); } finally { lock.current = false; if (mounted.current) setBusy(false); } }
    else if (lastRequest.current) void request(lastRequest.current.messages, lastRequest.current.files, lastRequest.current.forceGenerate);
  };

  const complete = products.length > 0 && progress.length === products.length && progress.every(item => item.complete);

  const thumbLabels = ['Cover', 'Banner', 'PDF · pagina 2', 'PDF · pagina 3', 'PDF · pagina 4'];

  return <motion.section className="ws-product-studio" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: reduced ? 0 : .45 }}>
    <header className="ws-studio-header">
      <div className="ws-studio-brand">
        <span className="ws-studio-brand-badge"><Sparkles /> Buddy Studio</span>
        <span className="ws-studio-brand-sub">Van idee naar digitaal product</span>
      </div>
      <button type="button" className="ws-studio-close-btn" onClick={onClose} aria-label="Sluit studio">
        <X />
      </button>
    </header>

    {!!job?.agents.length && <div className="ws-studio-decisions" aria-label="Agentvoortgang" aria-live="polite">{job.agents.map(agent => <span key={agent.role}>{agent.status === 'running' ? <Loader2 className="ws-spin" /> : agent.status === 'completed' ? <Check /> : <X />}{agent.role}</span>)}</div>}

    <div className={`ws-studio-buddy ${busy ? 'is-thinking' : ''}`} aria-hidden="true"><div className="ws-buddy-body"><span /><span /></div><div className="ws-buddy-orbit" /><i /><i /><i /></div>

    <div className="ws-studio-thread" role="log" aria-label="Product creation conversation" aria-live="polite">
      {!messages.length && <div className="ws-studio-empty"><h1>Wat wil je creëren?</h1><p>Vertel je idee. Buddy maakt de inhoud, PDF, previews en marketing en bewaart alles als concept.</p></div>}
      {messages.map((message, index) => <motion.div key={index} className={`ws-studio-message is-${message.role}`} initial={{ opacity: 0, y: reduced ? 0 : 14, scale: reduced ? 1 : 0.985 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: .3 }}><span>{message.role === 'user' ? <>Jij <User style={{ width: 13, height: 13, marginLeft: 4 }} /></> : <><Sparkles /> Wersee AI Studio</>}</span><p>{message.content}</p></motion.div>)}
      {reply && <div className="ws-studio-decisions">{reply.summary.map(item => <span key={item}><Check />{item}</span>)}</div>}
      {reply?.phase === 'question' && !busy && !error && <div className="ws-studio-suggestions">{reply.suggestions.map(suggestion => <button type="button" key={suggestion} onClick={() => send(suggestion)}>{suggestion}</button>)}<button type="button" className="ws-studio-make-now" onClick={() => void request(messages, [], true)}><Sparkles />Maak de producten direct</button></div>}
      {status && <p className="ws-studio-status" role="status">{busy && <Loader2 className="ws-spin" />}{status}</p>}
      {busy && job && ['queued', 'running'].includes(job.state) && <button type="button" className="ws-studio-close-btn" onClick={() => void werseeAi.updateStudioProject(job.id, 'cancel').catch(() => setError('Annuleren kon niet worden opgeslagen.'))}>Annuleren</button>}
      {error && <div className="ws-studio-error" role="alert"><p>{error}</p><button type="button" disabled={busy} onClick={() => void retry()}><RefreshCw />Opnieuw proberen</button></div>}
      
      {!!products.length && <div className="ws-studio-products">{products.map((product, index) => {
        const itemProg = progress[index];
        const allThumbs = itemProg?.thumbnailUrls || (itemProg?.coverUrl ? [itemProg.coverUrl] : []);
        const selectedIdx = activeThumb[index] || 0;
        const currentThumbUrl = allThumbs[selectedIdx] || itemProg?.coverUrl;
        const isBannerView = selectedIdx === 1 && allThumbs.length > 1;

        return <motion.article key={index} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: reduced ? 0 : index * .12 }}>
          <div className={`ws-studio-product-preview-wrap ${isBannerView ? 'is-banner' : ''}`} style={{ background: product.color }}>
            {currentThumbUrl ? (
              <img src={currentThumbUrl} alt={`${product.title} preview ${selectedIdx + 1}`} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#fff', gap: 14 }}>
                <FileText style={{ width: 44, height: 44 }} />
                <strong>{product.title}</strong>
              </div>
            )}
            <span className="ws-studio-badge-pill">{product.format.toUpperCase()} · STOREFRONT</span>
            {itemProg?.pageCount && <span className="ws-studio-pages-pill">{itemProg.pageCount} Pagina's PDF</span>}
          </div>

          {allThumbs.length > 1 && (
            <div className="ws-studio-thumb-tabs">
              {allThumbs.map((_, tIdx) => (
                <button
                  key={tIdx}
                  type="button"
                  className={`ws-studio-thumb-tab ${selectedIdx === tIdx ? 'is-active' : ''}`}
                  onClick={() => setActiveThumb({ ...activeThumb, [index]: tIdx })}
                >
                  {thumbLabels[tIdx] || `Preview ${tIdx + 1}`}
                </button>
              ))}
            </div>
          )}

          <section>
            <span>{product.category} · {product.format}</span>
            <h2>{product.title}</h2>
            <p>{product.shortDescription}</p>
            <strong>{new Intl.NumberFormat(undefined, { style: 'currency', currency: product.currency }).format(product.price)}<small>Adviesprijs voor storefront</small></strong>
            {itemProg?.complete ? (
              <Link to={`/create/digital/${itemProg.id}`}>
                Bekijk & Publiceer in Store <ArrowUpRight />
              </Link>
            ) : (
              <span style={{ fontSize: 12, color: 'var(--store-muted)' }}>{busy ? 'Genereren van bestanden…' : 'Wachten op afronding'}</span>
            )}
            {product.marketing && <button type="button" className="ws-store-primary" onClick={async () => {
              const { saveAs } = await import('file-saver');
              const marketing = product.marketing!;
              const content = `# ${product.title}\n\n## Lancering\n${marketing.launch}\n\n## Social posts\n${marketing.socialPosts.join('\n\n---\n\n')}\n\n## E-mails\n${marketing.emails.map(email => `### ${email.subject}\n${email.body}`).join('\n\n')}\n\n## Adviesprijs\n${marketing.pricingRationale}`;
              saveAs(new Blob([content], { type: 'text/markdown;charset=utf-8' }), 'marketing.md');
            }}>Download marketing</button>}
          </section>
        </motion.article>;
      })}</div>}

      {complete && <Link className="ws-studio-workspace" to="/workspace/management-products">Open je workspace overzicht <ArrowUpRight /></Link>}<div ref={end} />
    </div>

    {(!products.length || complete) && <motion.div className="ws-studio-compose-dock" style={{ position: 'fixed', left: surface.left, right: 0, bottom: surface.bottom }} initial={{ y: reduced ? 0 : 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: 'spring', stiffness: 130, damping: 23 }}><StudioComposer chat value={prompt} onChange={setPrompt} files={files} onFilesChange={setFiles} onSubmit={() => send()} busy={busy} /><p>{complete ? 'Vraag een wijziging. Buddy bewaart het vorige project en maakt een nieuwe versie.' : 'Buddy maakt inhoud, bestanden en concepten. Jij controleert en publiceert.'}</p></motion.div>}
  </motion.section>;
}

